import React, { useState } from 'react';
import '../screens/detail.css';
import logo from '../assets/STAMP-LOGO.webp';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { fetchEventDetail } from '../api/events';
import { createReservation } from '../api/reservations';
import { fetchMyVolunteerApplication } from '../api/volunteers';
import { useAuth } from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import VolunteerRequiredModal from '../components/VolunteerRequiredModal';
import { formatEventDateParts } from './eventDetailUtils';
import ReviewSection from '../components/reviews/ReviewSection';

export default function Detail() {
    const { pk } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, user } = useAuth();

    // null이면 모달을 닫은 상태, 그 외에는 자원봉사 신청서 상태를 담는다.
    const [volunteerGate, setVolunteerGate] = useState(null);

    // 이벤트 참가는 자원봉사자 전용이다 (events/views.py: EventReservations.post).
    // 백엔드는 admin/staff도 통과시키지만 is_staff는 프로필에 내려오지 않으므로,
    // 여기서는 role만 보고 어긋나는 경우는 아래 403 처리로 받는다.
    const roles = new Set(user?.role || []);
    const canRegister = roles.has('Volunteer') || roles.has('Admin');

    // 신청서 상태에 따라 안내 문구가 달라지므로, 모달을 열기 직전에 조회한다.
    const openVolunteerGate = async () => {
        let status = 'none';
        try {
            const application = await fetchMyVolunteerApplication();
            if (application?.status === 'pending' || application?.status === 'rejected') {
                status = application.status;
            }
        } catch {
            // 상태를 못 읽어도 안내 자체는 띄우는 편이 낫다.
        }
        setVolunteerGate(status);
    };

    const {
        data: event,
        isLoading,
        error,
    } = useQuery({
        queryKey: ['event', pk],
        queryFn: () => fetchEventDetail(pk),
        enabled: !!pk,
    });

    const registerMutation = useMutation({
        mutationFn: () => createReservation(pk),
        onSuccess: () => {
            alert('Reservation successful!');
            navigate('/mypage');
        },
        onError: (error) => {
            if (
                error.response?.status === 403 &&
                error.response?.data?.code === 'volunteer_required'
            ) {
                openVolunteerGate();
                return;
            }
            const message =
                error.response?.data?.error ||
                'Failed to register. Please try again.';
            alert(message);
        },
    });

    if (isLoading) return <LoadingSpinner />;
    if (error)
        return (
            <div className="error-message">Error loading event details.</div>
        );
    if (!event) return <div className="error-message">Event not found.</div>;

    // Data mapping
    const eventData = {
        time: event.start_date
            ? `${new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(event.end_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
            : 'TBD',
        location: event.location,
        title: event.title,
        description: event.description,
        image:
            event.photos && event.photos.length > 0
                ? event.photos[0].file
                : logo, // Use first photo or fallback
        registerUrl: '#', // Should be implemented later with actual reservation logic
    };

    const handleRegisterClick = async () => {
        if (!isAuthenticated) {
            if (
                window.confirm(
                    'You need to login to register. Go to login page?',
                )
            ) {
                navigate('/login', { state: { from: location } });
            }
            return;
        }

        if (!canRegister) {
            await openVolunteerGate();
            return;
        }

        if (window.confirm(`Do you want to register for ${eventData.title}?`)) {
            registerMutation.mutate();
        }
    };

    return (
        <div className="detail-page">
            <div className="detail-container">
                {/* Left Section — sticky, follows scroll */}
                <div className="detail-left-section">
                    <div className="detail-line"></div>

                    <div className="detail-date">
                        {(() => {
                            const parts = formatEventDateParts(event.start_date);
                            if (!parts) return 'TBD';
                            return (
                                <>
                                    <span>{parts.monthDay}</span>
                                    <span>{parts.year}</span>
                                </>
                            );
                        })()}
                    </div>

                    <div className="detail-location">{eventData.location}</div>
                    <div className="detail-time">{eventData.time}</div>

                    <div className="detail-line"></div>

                    <button
                        className="detail-register-btn"
                        onClick={handleRegisterClick}
                        disabled={registerMutation.isPending}
                    >
                        {registerMutation.isPending
                            ? 'Registering...'
                            : 'Register now!'}
                    </button>
                </div>

                {/* Right Section */}
                <div className="detail-right-section">
                    <div className="detail-image">
                        <img src={eventData.image} alt={eventData.title} />
                    </div>
                    <div className="detail-title">{eventData.title}</div>
                    <div className="detail-description">
                        {eventData.description}
                    </div>
                    <ReviewSection eventPk={pk} />
                </div>
            </div>

            {volunteerGate && (
                <VolunteerRequiredModal
                    status={volunteerGate}
                    onApply={() => {
                        setVolunteerGate(null);
                        navigate('/volunteer');
                    }}
                    onClose={() => setVolunteerGate(null)}
                />
            )}
        </div>
    );
}
